import type { PhotoAnalysis, PhotoMediumType, PhotoSourceType } from '../types';

export type QuickAction = {
  id: string;
  /** Short button label shown to the user */
  label: string;
  /** Safe instruction sent to the restoration model (emphasis within fidelity rules) */
  instruction: string;
};

const MAX_ACTIONS = 5;

function hasDamage(analysis: PhotoAnalysis, ...keys: string[]) {
  const d = (analysis.damageTypes || []).map((x) => String(x).toLowerCase());
  const c = (analysis.captureIssues || []).map((x) => String(x).toLowerCase());
  const all = [...d, ...c];
  return keys.some((k) => all.some((x) => x.includes(k)));
}

function isDigital(analysis: PhotoAnalysis) {
  return (
    analysis.photoType === 'digital-photo' ||
    analysis.sourceType === 'native-digital'
  );
}

function isMonochrome(analysis: PhotoAnalysis) {
  return (
    analysis.photoType === 'silver-gelatin-bw' ||
    analysis.photoType === 'sepia-albumen' ||
    analysis.photoType === 'cyanotype-monochrome' ||
    analysis.photoType === 'platinum-palladium' ||
    analysis.photoType === 'daguerreotype-tintype'
  );
}

function isFadedColor(analysis: PhotoAnalysis) {
  return (
    analysis.photoType === 'aged-c41-color' ||
    analysis.photoType === 'kodachrome-color' ||
    analysis.photoType === 'polaroid-instant' ||
    analysis.photoType === 'hand-tinted-portrait'
  );
}

/**
 * Deterministic, analysis-driven quick actions.
 * Display labels stay short; instructions remain fidelity-safe emphases.
 */
export function buildQuickActions(
  analysis: PhotoAnalysis | null | undefined,
  { isFirstRun }: { isFirstRun: boolean }
): QuickAction[] {
  if (!analysis) {
    return isFirstRun
      ? [
          {
            id: 'preserve-faces',
            label: 'Preserve faces and original proportions',
            instruction:
              'Emphasize exact composition, facial landmark lock, and identity preservation. Do not invent unsupported detail.',
          },
        ]
      : [
          {
            id: 'closer-to-original',
            label: 'Bring faces closer to the original',
            instruction:
              'Bring faces closer to the original source geometry and likeness. Do not invent detail or beautify.',
          },
        ];
  }

  const digital = isDigital(analysis);
  const candidates: QuickAction[] = [];

  if (isFirstRun) {
    candidates.push({
      id: 'preserve-faces',
      label: 'Preserve faces and original proportions',
      instruction:
        'Emphasize exact composition, facial landmark lock, and identity preservation throughout this restoration. Do not invent unsupported detail.',
    });

    if (analysis.hasGlareOrReflections || hasDamage(analysis, 'glare', 'reflection', 'lens-flare')) {
      candidates.push({
        id: 'reduce-glare',
        label: digital ? 'Reduce recoverable lens flare' : 'Reduce glare and reflections conservatively',
        instruction: digital
          ? 'Reduce recoverable lens flare using nearby surviving evidence. Do not invent facial or scene detail behind total obstruction.'
          : 'Reduce glare and surface reflections conservatively using nearby surviving photographic evidence. Do not invent unsupported detail.',
      });
    }

    if (!digital && hasDamage(analysis, 'scratch', 'crease', 'tear', 'fold', 'stain', 'crack')) {
      candidates.push({
        id: 'physical-damage',
        label: 'Prioritize scratches and crease repair',
        instruction:
          'Prioritize localized repair of scratches, creases, and physical print damage using nearby surviving evidence. Do not redraw intact areas or invent missing semantic detail.',
      });
    }

    if (!digital && isFadedColor(analysis)) {
      candidates.push({
        id: 'faded-color',
        label: 'Restore faded color conservatively',
        instruction:
          'Restore faded color balance conservatively. Do not invent highly saturated colors when evidence is uncertain.',
      });
    }

    if (!digital && isMonochrome(analysis)) {
      candidates.push({
        id: 'mono-tonality',
        label: 'Preserve original monochrome tonality',
        instruction:
          'Preserve the original monochrome/tonal palette. Do not colorize. Keep historical medium character.',
      });
    }

    if (digital && hasDamage(analysis, 'motion-blur', 'focus-soft', 'blur')) {
      candidates.push({
        id: 'digital-blur',
        label: 'Reduce blur without inventing detail',
        instruction:
          'Reduce recoverable blur only to the level supported by the source. Do not invent eyes, hair, skin texture, or micro-detail. Soft unresolved features must remain soft.',
      });
    }

    if (digital && hasDamage(analysis, 'noise', 'low-light')) {
      candidates.push({
        id: 'digital-noise',
        label: 'Reduce digital noise while preserving faces',
        instruction:
          'Reduce low-light digital noise while preserving exact facial geometry and identity. Do not invent pore-level detail or sharpen into synthetic texture.',
      });
    }

    if (analysis.hasPerspectiveDistortion) {
      candidates.push({
        id: 'perspective',
        label: 'Preserve composition after perspective correction',
        instruction:
          'After any perspective correction, preserve exact subject spacing, framing intent, and facial geometry. Do not restyle or recenter creatively.',
      });
    }
  } else {
    candidates.push({
      id: 'closer-to-original',
      label: 'Bring faces closer to the original',
      instruction:
        'Bring faces closer to the original source geometry and likeness. Do not invent detail, beautify, or change pose.',
    });

    candidates.push({
      id: 'natural-softness',
      label: 'Reduce over-sharpening / restore natural softness',
      instruction:
        'Reduce over-sharpening and restore natural softness supported by the source. Do not invent micro-detail.',
    });

    if (analysis.hasGlareOrReflections || hasDamage(analysis, 'glare', 'reflection', 'lens-flare')) {
      candidates.push({
        id: 'remaining-glare',
        label: 'Reduce remaining glare',
        instruction:
          'Reduce remaining glare or flare conservatively using nearby surviving evidence. Do not invent unsupported detail.',
      });
    }

    if (!digital && hasDamage(analysis, 'scratch', 'crease', 'tear', 'fold', 'stain', 'crack')) {
      candidates.push({
        id: 'remaining-damage',
        label: 'Repair remaining scratches or creases',
        instruction:
          'Repair remaining scratches or creases with localized defect repair only. Do not redraw intact faces or invent missing content.',
      });
    }

    if (!digital && isFadedColor(analysis)) {
      candidates.push({
        id: 'less-saturation',
        label: 'Reduce color saturation',
        instruction:
          'Reduce overly vivid color saturation while preserving relative tonal relationships from the source.',
      });
    }

    if (digital) {
      candidates.push({
        id: 'wb-cast',
        label: 'Correct color cast / white balance',
        instruction:
          'Correct color cast / white balance conservatively using evidence in the source. Do not invent highly saturated or stylized colors.',
      });
      if (hasDamage(analysis, 'noise', 'low-light', 'compression')) {
        candidates.push({
          id: 'noise-refine',
          label: 'Reduce noise without changing facial detail',
          instruction:
            'Reduce digital noise/compression artifacts without changing facial geometry or inventing micro-detail.',
        });
      }
    }

    if (analysis.hasBackgroundClutter) {
      candidates.push({
        id: 'frame',
        label: 'Preserve more of the original frame',
        instruction:
          'Preserve more of the original frame and avoid overly tight cropping. Keep composition faithful to the source.',
      });
    }
  }

  // Deduplicate by id while preserving priority order
  const seen = new Set<string>();
  const unique: QuickAction[] = [];
  for (const a of candidates) {
    if (seen.has(a.id)) continue;
    seen.add(a.id);
    unique.push(a);
    if (unique.length >= MAX_ACTIONS) break;
  }
  return unique;
}

export const SOURCE_TYPE_LABELS: Record<PhotoSourceType, string> = {
  'native-digital': 'Native Digital Photograph',
  'photographed-print': 'Phone Capture of Physical Print',
  'scanned-print': 'Scanned Physical Photograph',
  'screenshot-or-copy': 'Digital Copy / Screenshot',
  unknown: 'Unknown Source',
};

export const MEDIUM_LABELS_EXT: Partial<Record<PhotoMediumType, string>> = {
  'digital-photo': 'Native Digital Photograph',
};
